import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Brain, TrendingUp, Activity, BarChart3, Zap, Clock, Target, Shield, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { API_BASE as API } from '../config';

// Defensive string-coercion for fields whose backend shape may drift
// between "string" and "object with nested detail". Without this, the
// whole tab white-screens with React error #31 the moment the bot
// writes a structured reason object (regime/confidence/rsi/etc).
function safeText(v) {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
}

function StatCard({ label, value, sub, icon: Icon, color = "text-zinc-100" }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5 text-zinc-500" />}
        <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className={`text-xl font-mono font-semibold ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-600 mt-1 font-mono">{sub}</div>}
    </div>
  );
}

function ReasoningCard({ entry }) {
  const [expanded, setExpanded] = useState(false);
  // Regime is meant to be a plain string tag (bull/bear/ranging/…). If the
  // backend ever returns an object shape, coerce to a stable label so the
  // lookup into regimeColors still works.
  const regimeRaw = entry.regime;
  const regime = (typeof regimeRaw === "string" && regimeRaw) || "unknown";
  const regimeColors = {
    bull: "text-emerald-400 bg-emerald-400/10",
    bear: "text-red-400 bg-red-400/10",
    ranging: "text-amber-400 bg-amber-400/10",
    oversold_bounce: "text-cyan-400 bg-cyan-400/10",
    overbought: "text-orange-400 bg-orange-400/10",
    high_volatility: "text-purple-400 bg-purple-400/10",
    unknown: "text-zinc-400 bg-zinc-400/10",
  };
  const regimeStyle = regimeColors[regime] || regimeColors.unknown;

  return (
    <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-lg p-3 hover:border-zinc-700/60 transition-all">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <span className="text-sm font-mono font-bold text-white">{safeText(entry.symbol).replace("USDC","") || "?"}</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono uppercase ${regimeStyle}`}>{regime}</span>
          <span className="text-[10px] text-zinc-500 font-mono">RSI {safeText(entry.rsi) || "?"}</span>
          <span className="text-[10px] text-zinc-500 font-mono">conf {safeText(entry.confidence) || 0}%</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-zinc-600 font-mono">
            {entry.timestamp ? new Date(entry.timestamp).toLocaleString("el-GR", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" }) : ""}
          </span>
          {expanded ? <ChevronUp className="w-3 h-3 text-zinc-500" /> : <ChevronDown className="w-3 h-3 text-zinc-500" />}
        </div>
      </div>
      {expanded && (
        <div className="mt-3 space-y-2 text-[11px] font-mono text-zinc-400 border-t border-zinc-800 pt-3">
          {entry.action && <div><span className="text-zinc-500">Action:</span> <span className="text-white">{safeText(entry.action)}</span></div>}
          {entry.reason && (
            <div>
              <span className="text-zinc-500">Reason:</span>{" "}
              {typeof entry.reason === "object"
                ? <pre className="inline-block bg-zinc-900/80 rounded p-2 text-[10px] whitespace-pre-wrap">{JSON.stringify(entry.reason, null, 2)}</pre>
                : safeText(entry.reason)}
            </div>
          )}
          {entry.technicals && typeof entry.technicals === "object" && (
            <div className="grid grid-cols-2 gap-2 mt-1">
              {typeof entry.technicals.ema_9 === "number" && <div>EMA9: {entry.technicals.ema_9.toFixed(4)}</div>}
              {typeof entry.technicals.ema_21 === "number" && <div>EMA21: {entry.technicals.ema_21.toFixed(4)}</div>}
              {typeof entry.technicals.bb_upper === "number" && <div>BB Upper: {entry.technicals.bb_upper.toFixed(4)}</div>}
              {typeof entry.technicals.bb_lower === "number" && <div>BB Lower: {entry.technicals.bb_lower.toFixed(4)}</div>}
              {typeof entry.technicals.atr === "number" && <div>ATR: {entry.technicals.atr.toFixed(4)}</div>}
              {typeof entry.technicals.volatility === "number" && <div>Vol: {(entry.technicals.volatility * 100).toFixed(1)}%</div>}
            </div>
          )}
          {entry.news_sentiment && <div><span className="text-zinc-500">News:</span> {safeText(entry.news_sentiment)}</div>}
          {Array.isArray(entry.evolution_hints) && entry.evolution_hints.length > 0 && (
            <div><span className="text-zinc-500">Evolution Hints:</span> {entry.evolution_hints.map(safeText).join(", ")}</div>
          )}
        </div>
      )}
    </div>
  );
}

function ComparisonTable({ data }) {
  if (!data?.leaderboard) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px] font-mono">
        <thead>
          <tr className="text-zinc-500 border-b border-zinc-800">
            <th className="text-left py-2 px-2">#</th>
            <th className="text-left py-2 px-2">Strategy</th>
            <th className="text-right py-2 px-2">Score</th>
            <th className="text-right py-2 px-2">APY</th>
            <th className="text-right py-2 px-2">DD</th>
            <th className="text-right py-2 px-2">WR</th>
          </tr>
        </thead>
        <tbody>
          {data.leaderboard.map((s, i) => (
            <tr key={s.strategy} className={`border-b border-zinc-800/50 ${i === 0 ? "bg-emerald-500/5" : ""}`}>
              <td className="py-2 px-2 text-zinc-500">{i + 1}</td>
              <td className="py-2 px-2 text-white font-semibold">{s.strategy} {i === 0 && "★"}</td>
              <td className={`py-2 px-2 text-right ${s.composite_score > 0 ? "text-emerald-400" : "text-red-400"}`}>{s.composite_score?.toFixed(1)}</td>
              <td className={`py-2 px-2 text-right ${s.avg_apy > 0 ? "text-emerald-400" : "text-red-400"}`}>{s.avg_apy?.toFixed(1)}%</td>
              <td className="py-2 px-2 text-right text-amber-400">{s.avg_max_dd?.toFixed(1)}%</td>
              <td className="py-2 px-2 text-right text-zinc-300">{s.avg_win_rate?.toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function StrategyLabTab() {
  const { token } = useAuth();
  const [reasoning, setReasoning] = useState({ entries: [], total: 0 });
  const [stats, setStats] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [evoLog, setEvoLog] = useState({ entries: [], total: 0 });
  const [news, setNews] = useState({ articles: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [subTab, setSubTab] = useState("reasoning");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [rRes, sRes, cRes, eRes, nRes] = await Promise.allSettled([
        fetch(`${API}/api/trade-reasoning/recent?limit=30`, { headers }),
        fetch(`${API}/api/trade-reasoning/stats`, { headers }),
        fetch(`${API}/api/trade-reasoning/comparison`, { headers }),
        fetch(`${API}/api/trade-reasoning/evolution-log?limit=10`, { headers }),
        fetch(`${API}/api/trade-reasoning/news?limit=20`, { headers }),
      ]);
      if (rRes.status === "fulfilled" && rRes.value.ok) setReasoning(await rRes.value.json());
      if (sRes.status === "fulfilled" && sRes.value.ok) setStats(await sRes.value.json());
      if (cRes.status === "fulfilled" && cRes.value.ok) setComparison(await cRes.value.json());
      if (eRes.status === "fulfilled" && eRes.value.ok) setEvoLog(await eRes.value.json());
      if (nRes.status === "fulfilled" && nRes.value.ok) setNews(await nRes.value.json());
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const subTabs = [
    { id: "reasoning", label: "Trade Reasoning", icon: Brain },
    { id: "comparison", label: "Strategy Battle", icon: Target },
    { id: "evolution", label: "Evolution Log", icon: Zap },
    { id: "news", label: "News Feed", icon: Activity },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brain className="w-5 h-5 text-purple-400" />
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>Strategy Lab</h2>
          <span className="text-[10px] text-zinc-500 font-mono">AI-powered research engine</span>
        </div>
        <button onClick={fetchAll} className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors" disabled={loading}>
          <RefreshCw className={`w-4 h-4 text-zinc-400 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total Reasonings" value={stats.total} icon={Brain} color="text-purple-400" />
          <StatCard label="Avg Confidence" value={`${stats.avg_confidence}%`} icon={Target} color="text-cyan-400" />
          <StatCard label="Evolution Runs" value={evoLog.total} icon={Zap} color="text-amber-400" />
          <StatCard label="News Articles" value={news.total} icon={Activity} color="text-emerald-400" />
        </div>
      )}

      {/* Regime Distribution */}
      {stats?.regimes && Object.keys(stats.regimes).length > 0 && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-4">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-3">Market Regime Distribution</div>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(stats.regimes).sort((a, b) => b[1] - a[1]).map(([regime, count]) => {
              const colors = { bull: "bg-emerald-500", bear: "bg-red-500", ranging: "bg-amber-500", oversold_bounce: "bg-cyan-500", overbought: "bg-orange-500", high_volatility: "bg-purple-500" };
              const pct = ((count / stats.total) * 100).toFixed(0);
              return (
                <div key={regime} className="flex items-center gap-1.5 px-2 py-1 bg-zinc-800/60 rounded-md">
                  <div className={`w-2 h-2 rounded-full ${colors[regime] || "bg-zinc-500"}`} />
                  <span className="text-[10px] font-mono text-zinc-300">{regime}</span>
                  <span className="text-[10px] font-mono text-zinc-500">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-zinc-900/60 border border-zinc-800 rounded-lg p-1">
        {subTabs.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-mono transition-all ${subTab === t.id ? "bg-zinc-800 text-white" : "text-zinc-500 hover:text-zinc-300"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {subTab === "reasoning" && (
        <div className="space-y-2">
          {reasoning.entries.length === 0 ? (
            <div className="text-center py-10 text-zinc-500 font-mono text-sm">No trade reasoning data yet. Bot will generate reasoning on next trade.</div>
          ) : (
            reasoning.entries.map((e, i) => <ReasoningCard key={i} entry={e} />)
          )}
        </div>
      )}

      {subTab === "comparison" && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-4">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-3">Strategy Battle: TV Strategies vs DCA Bot</div>
          {comparison ? <ComparisonTable data={comparison} /> : (
            <div className="text-center py-10 text-zinc-500 font-mono text-sm">No comparison data. Run compare_strategies.py on the server.</div>
          )}
        </div>
      )}

      {subTab === "evolution" && (
        <div className="space-y-3">
          {evoLog.entries.length === 0 ? (
            <div className="text-center py-10 text-zinc-500 font-mono text-sm">No evolution runs yet. Next run scheduled daily at 06:00 UTC.</div>
          ) : (
            evoLog.entries.map((run, i) => (
              <div key={i} className="bg-zinc-900/40 border border-zinc-800/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-mono font-bold text-white">Evolution Run #{evoLog.total - i}</span>
                  <span className="text-[10px] text-zinc-500 font-mono">
                    {run.timestamp ? new Date(run.timestamp).toLocaleString("el-GR") : run.date || ""}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px] font-mono">
                  {run.best_apy != null && <div><span className="text-zinc-500">Best APY:</span> <span className="text-emerald-400">{run.best_apy?.toFixed(1)}%</span></div>}
                  {run.best_dd != null && <div><span className="text-zinc-500">Max DD:</span> <span className="text-amber-400">{run.best_dd?.toFixed(1)}%</span></div>}
                  {run.mutations_tested != null && <div><span className="text-zinc-500">Mutations:</span> <span className="text-zinc-300">{run.mutations_tested}</span></div>}
                  {run.improved != null && <div><span className="text-zinc-500">Improved:</span> <span className={run.improved ? "text-emerald-400" : "text-zinc-500"}>{run.improved ? "Yes" : "No"}</span></div>}
                </div>
                {run.new_config && (
                  <div className="mt-2 text-[10px] font-mono text-zinc-500 bg-zinc-900/60 rounded p-2 overflow-x-auto">
                    <pre>{JSON.stringify(run.new_config, null, 2)}</pre>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {subTab === "news" && (
        <div className="space-y-2">
          {news.articles.length === 0 ? (
            <div className="text-center py-10 text-zinc-500 font-mono text-sm">No news articles yet. News scraper runs every 6 hours.</div>
          ) : (
            news.articles.map((article, i) => (
              <div key={i} className="bg-zinc-900/40 border border-zinc-800/50 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <a href={article.url || article.link || "#"} target="_blank" rel="noopener noreferrer"
                    className="text-sm font-semibold text-zinc-200 hover:text-white transition-colors line-clamp-1">
                    {article.title}
                  </a>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono ${
                    article.sentiment === "bullish" ? "text-emerald-400 bg-emerald-400/10" :
                    article.sentiment === "bearish" ? "text-red-400 bg-red-400/10" :
                    "text-zinc-400 bg-zinc-400/10"
                  }`}>{article.sentiment || "neutral"}</span>
                </div>
                <div className="flex items-center gap-3 mt-1.5">
                  <span className="text-[10px] text-zinc-500 font-mono">{article.source || ""}</span>
                  <span className="text-[10px] text-zinc-600 font-mono">
                    {article.published ? new Date(article.published).toLocaleString("el-GR", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" }) : ""}
                  </span>
                  {article.coins && article.coins.length > 0 && (
                    <div className="flex gap-1">
                      {article.coins.slice(0, 4).map(c => (
                        <span key={c} className="text-[9px] px-1.5 py-0.5 bg-zinc-800 rounded text-zinc-400 font-mono">{c}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
